import React, { useState, useMemo } from "react";
import { Button as MuiButton } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@/components/DataGrid/DataGrid";
import { NoRowsOverlay } from "@/features/dmManagement/components/NoRowsOverlay";
import type { Order } from "@/features/dmManagement/types";
import { formatNumberWithComma } from "@/utils/formatNumber";

const OrderNoRowsOverlay = (): JSX.Element => (
  <NoRowsOverlay message="注文データがありません" />
);

const dataGridInitialState = {
  sorting: { sortModel: [{ field: "createdAt", sort: "desc" as const }] },
};

const dataGridComponents = { NoRowsOverlay: OrderNoRowsOverlay } as const;

interface OrderListGridProps {
  orders: Order[];
  isLoading: boolean;
}

/**
 * 注文一覧グリッド
 * 注文データをDataGridで一覧表示し、詳細画面への遷移リンクを提供する
 */
export const OrderListGrid: React.FC<OrderListGridProps> = ({
  orders,
  isLoading,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "taskId",
        headerName: "識別番号",
        width: 120,
        sortable: false,
      },
      {
        field: "taskName",
        headerName: "識別名",
        width: 200,
        sortable: false,
      },
      {
        field: "quantity",
        headerName: "注文件数",
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) =>
          formatNumberWithComma(params.value as number),
      },
      {
        field: "createdAt",
        headerName: "注文日時",
        width: 180,
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as string;
          if (!value) return "";
          return format(new Date(value), "yyyy/MM/dd HH:mm");
        },
      },
      {
        field: "orderStatusDisplay",
        headerName: "注文状況",
        width: 120,
        sortable: false,
      },
      {
        field: "action",
        headerName: "Action",
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <MuiButton
            variant="outlined"
            size="small"
            onClick={() => {
              const order = params.row as Order;
              navigate(`/dmManagement/orders/${order.id}`);
            }}
          >
            詳細
          </MuiButton>
        ),
      },
    ],
    [navigate]
  );

  return (
    <DataGrid
      stickyHeader
      rows={orders}
      columns={columns}
      loading={isLoading}
      page={page}
      pageSize={100}
      onPageChange={(newPage: number) => {
        setPage(newPage);
      }}
      rowsPerPageOptions={[100]}
      initialState={dataGridInitialState}
      components={dataGridComponents}
    />
  );
};
